import Card, { type CardProps as MuiCardProps } from '@mui/material/Card';
import { useTheme } from '@mui/material/styles';
import { AnimatePresence, m } from 'motion/react';
import { useId } from 'react';
import {
  FloatCardPositioningProvider,
  type InitialPosition,
} from './use-positioning';

type Anchor = 'top' | 'right' | 'bottom' | 'left';

interface FloatingCardProps {
  open: boolean;
  onClose: () => void;
  CardProps?: MuiCardProps;
  children?: React.ReactNode;
  initialWidth?: number;
  initialHeight?: number;
  initialPosition?: InitialPosition;
  // Direction the card slides in from on open (and out on close). When
  // omitted, falls back to the historical behaviour: slide from the top if
  // `initialPosition` is top-anchored, otherwise from the bottom.
  anchor?: Anchor;
}

function getEntryOffset(
  anchor: Anchor | undefined,
  initialPosition: InitialPosition | undefined,
  targetX: number,
  targetY: number,
): { x: number; y: number } {
  switch (anchor) {
    case 'left':
      return { x: -window.innerWidth, y: targetY };
    case 'right':
      return { x: window.innerWidth, y: targetY };
    case 'top':
      return { x: targetX, y: -window.innerHeight };
    case 'bottom':
      return { x: targetX, y: window.innerHeight };
    default:
      if (initialPosition && 'top' in initialPosition) {
        return { x: targetX, y: -window.innerHeight };
      }
      return { x: targetX, y: window.innerHeight };
  }
}

export function FloatingCard({
  open,
  onClose,
  CardProps,
  children,
  initialWidth = 400,
  initialHeight = 400,
  initialPosition,
  anchor,
}: FloatingCardProps) {
  const { zIndex } = useTheme();
  const id = useId();

  return (
    <FloatCardPositioningProvider
      open={open}
      initialWidth={initialWidth}
      initialHeight={initialHeight}
      initialPosition={initialPosition}
    >
      {({ x, y, width, height, resizeHandlers, moveActive }) => {
        const entry = getEntryOffset(anchor, initialPosition, x, y);
        return (
          <AnimatePresence>
            {open && (
              <m.div
                key={id}
                initial={{ x: entry.x, y: entry.y, scale: 1 }}
                animate={{ x, y, scale: 1 }}
                exit={{ x, y, scale: 0 }}
                transition={{
                  ease: moveActive ? 'linear' : 'easeInOut',
                  duration: moveActive ? 0 : 0.3,
                }}
                style={{
                  position: 'fixed',
                  width,
                  height,
                  top: 0,
                  left: 0,
                  zIndex: zIndex.modal,
                  willChange: 'transform',
                }}
              >
                <Card
                  {...CardProps}
                  style={{ height: '100%' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      e.preventDefault();
                      onClose();
                    }

                    CardProps?.onKeyDown?.(e);
                  }}
                >
                  {children}
                </Card>
                {resizeHandlers}
              </m.div>
            )}
          </AnimatePresence>
        );
      }}
    </FloatCardPositioningProvider>
  );
}
